```vue
<script setup lang="ts">
import { ref, computed } from 'vue';
import { db } from '../firebase/config';
import { deleteDoc, getDocs, collection, writeBatch } from 'firebase/firestore';
import { useNotificationStore } from '../stores/notificationStore';
import { useAuthStore } from '../stores/authStore';

const notificationStore = useNotificationStore();
const authStore = useAuthStore();

const showConfirmation = ref(false);
const confirmationText = ref('');
const processing = ref(false);

// Secciones disponibles para blanquear
const sections = {
  products: {
    name: 'Productos',
    collections: ['products', 'product_laboratories'],
    description: 'Elimina todos los productos y sus relaciones con laboratorios'
  },
  categories: {
    name: 'Categorías',
    collections: ['categories'],
    description: 'Elimina todas las categorías'
  },
  configurations: {
    name: 'Configuraciones',
    collections: ['configurations'],
    description: 'Elimina todas las configuraciones del sistema'
  },
  laboratories: {
    name: 'Laboratorios',
    collections: ['laboratories'],
    description: 'Elimina todos los laboratorios'
  }
};

// Estado para las secciones seleccionadas
const selectedSections = ref<string[]>([]);

const resetDatabase = async () => {
  if (!authStore.user) {
    notificationStore.error('Debe iniciar sesión para realizar esta operación');
    return;
  }

  if (confirmationText.value !== 'BLANQUEAR') {
    notificationStore.error('El texto de confirmación no coincide');
    return;
  }

  if (selectedSections.value.length === 0) {
    notificationStore.error('Debe seleccionar al menos una sección');
    return;
  }

  try {
    processing.value = true;

    // Recopilar todas las colecciones a limpiar
    const collectionsToReset = selectedSections.value.reduce((acc, sectionKey) => {
      const section = sections[sectionKey as keyof typeof sections];
      return [...acc, ...section.collections];
    }, [] as string[]);

    // Usar batch para operaciones atómicas
    const batch = writeBatch(db);

    // Eliminar documentos de cada colección
    for (const collectionName of collectionsToReset) {
      const querySnapshot = await getDocs(collection(db, collectionName));
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
    }

    // Ejecutar el batch
    await batch.commit();

    notificationStore.success('Secciones seleccionadas blanqueadas correctamente');
    showConfirmation.value = false;
    confirmationText.value = '';
    selectedSections.value = [];

  } catch (error) {
    console.error('Error resetting database:', error);
    notificationStore.error('Error al blanquear las secciones seleccionadas');
  } finally {
    processing.value = false;
  }
};

// Seleccionar todas las secciones
const selectAll = () => {
  selectedSections.value = Object.keys(sections);
};

// Deseleccionar todas las secciones
const deselectAll = () => {
  selectedSections.value = [];
};

// Verificar si todas las secciones están seleccionadas
const allSelected = computed(() => {
  return selectedSections.value.length === Object.keys(sections).length;
});
</script>

<template>
  <div class="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
    <h3 class="text-lg font-semibold text-slate-800 mb-4">Blanquear Base de Datos</h3>
    
    <div class="space-y-6">
      <!-- Selección de secciones -->
      <div class="space-y-4">
        <div class="flex justify-between items-center">
          <h4 class="text-sm font-medium text-slate-700">Seleccione las secciones a blanquear</h4>
          <button
            @click="allSelected ? deselectAll() : selectAll()"
            class="text-sm text-indigo-600 hover:text-indigo-800"
          >
            {{ allSelected ? 'Deseleccionar todo' : 'Seleccionar todo' }}
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="(section, key) in sections"
            :key="key"
            class="border rounded-lg p-4 hover:bg-slate-50 transition-colors cursor-pointer"
            :class="{ 'border-indigo-500 bg-indigo-50': selectedSections.includes(key) }"
            @click="selectedSections.includes(key) 
              ? selectedSections = selectedSections.filter(s => s !== key)
              : selectedSections.push(key)"
          >
            <div class="flex items-start space-x-3">
              <input
                type="checkbox"
                :checked="selectedSections.includes(key)"
                class="mt-1 rounded text-indigo-600 focus:ring-indigo-500"
                @click.stop
              />
              <div>
                <h5 class="text-sm font-medium text-slate-800">{{ section.name }}</h5>
                <p class="text-xs text-slate-600 mt-1">{{ section.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Advertencia -->
      <div class="bg-red-50 p-4 rounded-lg">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">
              Advertencia: Esta operación es irreversible
            </h3>
            <div class="mt-2 text-sm text-red-700">
              <p>Se eliminarán permanentemente los siguientes datos:</p>
              <ul class="list-disc list-inside mt-2">
                <template v-if="selectedSections.length === 0">
                  <li class="text-slate-500 italic">Ninguna sección seleccionada</li>
                </template>
                <template v-else>
                  <li v-for="key in selectedSections" :key="key">
                    {{ sections[key as keyof typeof sections].name }}
                  </li>
                </template>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Botones de acción -->
      <div v-if="!showConfirmation" class="flex justify-end">
        <button
          @click="showConfirmation = true"
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          :disabled="processing || selectedSections.length === 0"
        >
          Blanquear Secciones Seleccionadas
        </button>
      </div>

      <!-- Confirmación -->
      <div v-else class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">
            Para confirmar, escriba "BLANQUEAR" en mayúsculas
          </label>
          <input
            v-model="confirmationText"
            type="text"
            class="input-field"
            placeholder="BLANQUEAR"
            :disabled="processing"
          />
        </div>

        <div class="flex justify-end space-x-4">
          <button
            @click="showConfirmation = false"
            class="px-4 py-2 text-slate-600 hover:text-slate-800"
            :disabled="processing"
          >
            Cancelar
          </button>
          <button
            @click="resetDatabase"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            :disabled="processing || confirmationText !== 'BLANQUEAR' || selectedSections.length === 0"
          >
            {{ processing ? 'Blanqueando...' : 'Confirmar Blanqueo' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
```